import React, { useEffect, useState, createContext, Dispatch, SetStateAction, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, Grid, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { Button, MdSpace } from 'm2m.components';

import { RootState } from 'store';
import { GrupoAcesso, IAditamento, FuncaoParticipante } from 'common/genericTypes';

import { AditamentoListItem } from './aditamentoListItem';
import { DrawerAprovarRejeitar } from './drawerAprovarRejeitar';

export type AditamentosContextType = {
  selected: IAditamento;
  setSelected: Dispatch<SetStateAction<any>>;
  checked: boolean;
  setChecked: Dispatch<SetStateAction<any>>;
  openValidationDrawer: boolean;
  actionType: 'aprovar' | 'rejeitar' | '';
  setOpenValidationDrawer: Dispatch<SetStateAction<any>>;
  isUsuarioOperacoes: boolean;
  refreshList: () => Promise<void>;
};

export const aditamentoInitialState = {
  id: 0,
  aditamentoGuid: '',
  codigoInstrumentoFinanceiro: '',
  aditamentoCount: 0,
  instrumentoFinanceiroId: '',
  statusAditamentoId: 1,
  tipoInstrumentoFinanceiro: '',
  new: '',
  old: '',
  justificativa: '',
  nomeUsuario: '',
  emissorId: '',
  emissorRazaoSocial: '',
  dataDeCriacao: '',
  dataUltimaAlteracao: '',
  aditamentoDocumentId: 0,
  instrumentoFinanceiroGuid: '',
  aditamentoDocumentGuid: ''
};

export const AditamentoContext = createContext<AditamentosContextType | null>(null);
const Aditamentos: React.FC = () => {
  const dispatch = useDispatch();

  const [aditamentos, setAditamentos] = useState<Array<IAditamento>>();
  const [loading, setLoading] = useState<boolean>(false);
  const [openValidationDrawer, setOpenValidationDrawer] = useState<boolean>(false);
  const [selected, setSelected] = useState<IAditamento>(aditamentoInitialState);
  const [checked, setChecked] = useState<boolean>(false);
  const [actionType, setActionType] = useState<AditamentosContextType['actionType']>('');

  const isUsuarioOperacoes =
    useSelector((state: RootState) => state.auth.usuario.grupoAcesso) === GrupoAcesso.Operacoes;
  const isUsuarioAgenteFiduciario =
    useSelector((state: RootState) => state.auth.usuario.participante.funcao) === FuncaoParticipante.AgenteFiduciario;
  const isUsuarioEscriturador =
    useSelector((state: RootState) => state.auth.usuario.participante.funcao) === FuncaoParticipante.Escriturador;

  const getAditamentosApi = useSelector((state: RootState) => state.aditamentosApi.getAditamentoList);
  const getAditamentos = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await getAditamentosApi(dispatch, { page: 0, pageSize: 80000 });
      setAditamentos(resp.result || []);
    } catch (error) {
      setAditamentos([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, getAditamentosApi]);

  useEffect(() => {
    getAditamentos();
  }, [getAditamentos]);
  return (
    <AditamentoContext.Provider
      value={{
        selected,
        setSelected,
        checked,
        setChecked,
        openValidationDrawer,
        setOpenValidationDrawer,
        actionType,
        isUsuarioOperacoes,
        refreshList: getAditamentos,
      }}
    >
      <div style={{ marginRight: 40, paddingTop: 40 }}>
        {(isUsuarioOperacoes || isUsuarioAgenteFiduciario || isUsuarioEscriturador) && (
          <Grid container spacing={2} justify={'flex-end'}>
            <Grid item>
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={() => {
                  setOpenValidationDrawer(true);
                  setActionType('aprovar');
                }}
                disabled={selected === aditamentoInitialState}
              >
                <CheckCircleIcon fontSize="small" style={{ marginRight: '10px' }} />
                Aprovar
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                size="small"
                color="error"
                onClick={() => {
                  setOpenValidationDrawer(true);
                  setActionType('rejeitar');
                }}
                disabled={selected === aditamentoInitialState}
              >
                <CheckCircleIcon fontSize="small" style={{ marginRight: '10px' }} />
                Rejeitar
              </Button>
            </Grid>
          </Grid>
        )}
        <MdSpace />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : aditamentos && aditamentos.length > 0 ? (
          aditamentos.map((item) => <AditamentoListItem key={item.id} aditamento={item} />)
        ) : (
          <Typography variant="body2">Nenhum aditamento encontrado.</Typography>
        )}
        <MdSpace />

        <DrawerAprovarRejeitar />
      </div>
    </AditamentoContext.Provider>
  );
};

export default Aditamentos;
